import {
  Home,
  User,
  Settings,
  Database,
  Truck,
  ChevronRight,
  LogOut,
  ChevronsUpDown,
  Link as LinkIcon,
  X,
  FileText,
  UserPlus,
  MapPin,
  Navigation,
  Route,
  CreditCard,
  BarChart3,
  Lock,
  LayoutDashboard,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "./ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Input } from "./ui/input";
import { useState } from "react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import { shouldHideSubscriptionPurchase } from "../utils/platform";
import { usePermissions } from "../hooks/usePermissions";

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasRole } = useAuth();
  const { can, PERMISSIONS } = usePermissions();
  const { isMobile, setOpenMobile } = useSidebar();

  // Check if user is admin (not superadmin)
  const isAdmin =
    hasRole("admin") && !hasRole("super_admin") && !hasRole("superadmin");
  const normalizedRole = user?.role?.toLowerCase() || "";
  const isSuperAdmin =
    normalizedRole === "superadmin" || normalizedRole === "super_admin";
  const isCustomer =
    normalizedRole === "customer" ||
    normalizedRole === "user" ||
    normalizedRole.includes("customer");
  const hidePaymentRoutes = shouldHideSubscriptionPurchase();

  // Search state for filtering menu items
  const [searchQuery, setSearchQuery] = useState("");

  // Debug: Log the role to console
  console.log("Current user role:", user?.role);
  console.log("Current user email:", user?.email);
  console.log(
    "Role check for invoices:",
    user?.role?.toLowerCase() === "superadmin" ||
      user?.role?.toLowerCase() === "super_admin"
  );

  // Dynamic panel text based on role
  const getPanelText = () => {
    const role = user?.role?.toLowerCase() || "admin";
    console.log("Panel text for role:", role);

    switch (role) {
      case "superadmin":
      case "super_admin":
        return "Super Admin Panel";
      case "admin":
        return "Admin Panel";
      default:
        return "Admin Panel";
    }
  };

  const handleLogout = async () => {
    try {
      // Use the AuthContext logout method which handles API call and cleanup
      await logout();
      if (isMobile) {
        setOpenMobile(false);
      }
      // Navigate to root which will redirect to login via ProtectedRoute
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
      // Even if logout fails, try to navigate away
      if (isMobile) {
        setOpenMobile(false);
      }
      navigate("/", { replace: true });
    }
  };

  // Filter menu items based on search query
  const filterMenuItem = (itemName, subItems = []) => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();

    // Check if main item matches
    if (itemName.toLowerCase().includes(query)) {
      return true;
    }

    // Check if any sub-item matches
    if (subItems.length > 0) {
      return subItems.some((subItem) => subItem.toLowerCase().includes(query));
    }

    return false;
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  return (
    <Sidebar className="">
      <SidebarHeader className="bg-muted/70">
        <div className="flex items-center gap-2 px-2 sm:px-4">
          <div className="flex items-center justify-center">
            <Truck className="size-3 sm:size-4" />
          </div>
          <div className="grid flex-1 text-left text-xs sm:text-sm leading-tight p-3">
            <span className="truncate font-semibold text-sidebar-foreground">
              TrackYourWheelz
            </span>
            <span className="truncate text-xs text-sidebar-foreground/70">
              {getPanelText()}
            </span>
          </div>
        </div>

        {/* Search Menu Items */}
        <div className="px-2 sm:px-4 pb-3 pt-2">
          <div className="relative">
            <Input
              type="text"
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-8 h-9 bg-muted/50 border-muted-foreground/20  rounded-lg border-1"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="bg-muted/70">
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Dashboard */}
              {filterMenuItem("Dashboard") && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === "/admin/dashboard"}
                  >
                    <Link
                      to="/admin/dashboard"
                      className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground"
                    >
                      <Home className="size-4" />
                      <span>Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

            

              {/* Recharge - Only show for admin (not superadmin) */}
              {isAdmin && filterMenuItem("Recharge") && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === "/admin/recharge"}
                  >
                    <Link
                      to="/admin/recharge"
                      className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground"
                    >
                      <CreditCard className="size-4" />
                      <span>Recharge</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* Accounts - Only show if user has permission */}
              {can(PERMISSIONS.VIEW_ACCOUNTS) &&
                filterMenuItem("Accounts", [
                  "All Accounts",
                  "Add Account",
                ]) && (
                  <Collapsible
                    defaultOpen={false}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton>
                          <User className="size-4" />
                          <span>Accounts</span>
                          <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {can(PERMISSIONS.VIEW_ACCOUNTS) && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={
                                  location.pathname === "/admin/accounts"
                                }
                              >
                                <Link
                                  to="/admin/accounts"
                                  className="no-underline"
                                >
                                  <span>All Accounts</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                          {can(PERMISSIONS.ADD_ACCOUNT) && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={
                                  location.pathname === "/admin/accounts/add"
                                }
                              >
                                <Link
                                  to="/admin/accounts/add"
                                  className="no-underline"
                                >
                                  <span>Add Account</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )}

              {/* DeviceTypes - Only show if user has permission */}
              {can(PERMISSIONS.VIEW_DEVICE_TYPES) &&
                filterMenuItem("DeviceTypes", [
                  "All DeviceTypes",
                  "Add DeviceTypes",
                ]) && (
                  <Collapsible
                    defaultOpen={false}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton>
                          <Database className="size-4" />
                          <span>DeviceTypes</span>
                          <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {can(PERMISSIONS.VIEW_DEVICE_TYPES) && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={
                                  location.pathname === "/admin/device-types"
                                }
                              >
                                <Link
                                  to="/admin/device-types"
                                  className="no-underline"
                                >
                                  <span>All DeviceTypes</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                          {can(PERMISSIONS.ADD_DEVICE_TYPE) && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={
                                  location.pathname ===
                                  "/admin/device-types/add"
                                }
                              >
                                <Link
                                  to="/admin/device-types/add"
                                  className="no-underline"
                                >
                                  <span>Add DeviceTypes</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )}

              {/* Devices - Only show if user has permission */}
              {can(PERMISSIONS.VIEW_DEVICES) &&
                filterMenuItem("Devices", ["All Devices", "Add Devices"]) && (
                  <Collapsible
                    defaultOpen={false}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton>
                          <Settings className="size-4" />
                          <span>Devices</span>
                          <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {can(PERMISSIONS.VIEW_DEVICES) && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={
                                  location.pathname === "/admin/devices"
                                }
                              >
                                <Link
                                  to="/admin/devices"
                                  className="no-underline"
                                >
                                  <span>All Devices</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                          {can(PERMISSIONS.ADD_DEVICE) && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={
                                  location.pathname === "/admin/devices/add"
                                }
                              >
                                <Link
                                  to="/admin/devices/add"
                                  className="no-underline"
                                >
                                  <span>Add Devices</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )}

              {/* Device Assignments - Only show if user has permission and is not superadmin */}
              {!isSuperAdmin &&
                can(PERMISSIONS.VIEW_DEVICE_ASSIGNMENTS) &&
                filterMenuItem("Device Assignments", [
                  "All Assignments",
                  "Assign Device",
                ]) && (
                  <Collapsible
                    defaultOpen={false}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton>
                          <LinkIcon className="size-4" />
                          <span>Device Assignments</span>
                          <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {can(PERMISSIONS.VIEW_DEVICE_ASSIGNMENTS) && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={
                                  location.pathname ===
                                  "/admin/device-assignments/all"
                                }
                              >
                                <Link
                                  to="/admin/device-assignments/all"
                                  className="no-underline"
                                >
                                  <span>All Assignments</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                          {can(PERMISSIONS.ADD_DEVICE_ASSIGNMENT) && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={
                                  location.pathname ===
                                  "/admin/device-assignments"
                                }
                              >
                                <Link
                                  to="/admin/device-assignments"
                                  className="no-underline"
                                >
                                  <span>Assign Device</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )}

              {/* Customer Device Assignments - Admin only */}
              {isAdmin &&
                filterMenuItem("Customer Device Assignments", [
                  "Customer Assignments",
                  "Assign to Customer",
                ]) && (
                  <Collapsible
                    defaultOpen={false}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton>
                          <UserPlus className="size-4" />
                          <span>Customer Assignment</span>
                          <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={
                                location.pathname ===
                                "/admin/customer-device-assignments"
                              }
                            >
                              <Link
                                to="/admin/customer-device-assignments"
                                className="no-underline"
                              >
                                <span>All Assignments</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={
                                location.pathname ===
                                "/admin/customer-device-assignments/add"
                              }
                            >
                              <Link
                                to="/admin/customer-device-assignments/add"
                                className="no-underline"
                              >
                                <span>Assign to Customer</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )}

              {/* Quotes - Only show if user has permission */}
              {can(PERMISSIONS.VIEW_QUOTES) &&
                filterMenuItem("Invoices", ["All Invoices", "Add Invoice"]) && (
                  <Collapsible
                    defaultOpen={false}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton>
                          <FileText className="size-4" />
                          <span>Quotes</span>
                          <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {can(PERMISSIONS.VIEW_QUOTES) && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={location.pathname === "/admin/quotes"}
                              >
                                <Link
                                  to="/admin/quotes"
                                  className="no-underline"
                                >
                                  <span>All Quotes</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                          {can(PERMISSIONS.ADD_QUOTE) && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={
                                  location.pathname === "/admin/quotes/add"
                                }
                              >
                                <Link
                                  to="/admin/quotes/add"
                                  className="no-underline"
                                >
                                  <span>Add Quote</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )}
              {/* Invoices - quick access for admin role */}
              {isAdmin && filterMenuItem("Invoices") && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname.startsWith("/admin/invoices")}
                  >
                    <Link
                      to="/admin/invoices"
                      className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground"
                    >
                      <FileText className="size-4" />
                      <span>Invoices</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {/* Invoices - Only show for superadmin */}
              {(user?.role?.toLowerCase() === "superadmin" ||
                user?.role?.toLowerCase() === "super_admin") &&
                filterMenuItem("Invoices", ["All Invoices", "Add Invoice"]) && (
                  <Collapsible
                    defaultOpen={false}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton>
                          <FileText className="size-4" />
                          <span>Invoice</span>
                          <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={location.pathname === "/admin/invoices"}
                            >
                              <Link
                                to="/admin/invoices"
                                className="no-underline"
                              >
                                <span>All Invoices</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={
                                location.pathname === "/admin/invoices/add"
                              }
                            >
                              <Link
                                to="/admin/invoices/add"
                                className="no-underline"
                              >
                                <span>Add Invoice</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )}

              {/* Customer Device Assignments - Only show for superadmin */}
              {(user?.role?.toLowerCase() === "superadmin" ||
                user?.role?.toLowerCase() === "super_admin") &&
                filterMenuItem("Customer Assignment", ["All Assignments"]) && (
                  <Collapsible
                    defaultOpen={false}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton>
                          <UserPlus className="size-4" />
                          <span>Customer Assignment</span>
                          <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={
                                location.pathname ===
                                "/admin/customer-device-assignments"
                              }
                            >
                              <Link
                                to="/admin/customer-device-assignments"
                                className="no-underline"
                              >
                                <span>All Assignments</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )}

              {/* Reports - Superadmin only */}
              {isSuperAdmin && filterMenuItem("Reports") && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === "/admin/report"}
                  >
                    <Link
                      to="/admin/report"
                      className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground"
                    >
                      <BarChart3 className="size-4" />
                      <span>Reports</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {isSuperAdmin && !hidePaymentRoutes && filterMenuItem("Subscription plans") && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === "/admin/subscription-plans"}
                  >
                    <Link
                      to="/admin/subscription-plans"
                      className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground"
                    >
                      <CreditCard className="size-4" />
                      <span>Subscription plans</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* All Vehicles Dashboard - Only for customers (not admin/superadmin) */}
              {isCustomer && filterMenuItem("All Vehicles Dashboard", ["Map Dashboard", "Vehicle Dashboard"]) && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === "/admin/map"}
                  >
                    <Link
                      to="/admin/map"
                      className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground"
                    >
                      <LayoutDashboard className="size-4" />
                      <span>All Vehicles Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {/* Map Dashboard - Only for customers (not admin/superadmin) */}
              {isCustomer && filterMenuItem("Map Dashboard", ["Map Dashboard"]) && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === "/admin/map/dashboard"}
                  >
                    <Link
                      to="/admin/map/dashboard"
                      className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground"
                    >
                      <MapPin className="size-4" />
                      <span>Map Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* Customer Devices - Only show for superadmin */}
              {(user?.role?.toLowerCase() === "superadmin" ||
                user?.role?.toLowerCase() === "super_admin") &&
                filterMenuItem("Tracking", ["Trip History"]) && (
                  <Collapsible
                    defaultOpen={false}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton>
                          <Navigation className="size-4" />
                          <span>Tracking</span>
                          <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={
                                location.pathname === "/admin/map/trips"
                              }
                            >
                              <Link
                                to="/admin/map/trips"
                                className="no-underline"
                              >
                                <Route className="size-4" />
                                <span>Trip History</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )}
              {!isSuperAdmin && isCustomer && filterMenuItem("Tracking") && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname.startsWith("/admin/map")}
                  >
                    <Link
                      to="/admin/map/trips"
                      className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground"
                    >
                      <Navigation className="size-4" />
                      <span>Tracking</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* Profile */}
              {filterMenuItem("Profile", ["Settings"]) && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === "/admin/profile"}
                  >
                    <Link
                      to="/admin/profile"
                      className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground"
                    >
                      <User className="size-4" />
                      <span>Profile</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {filterMenuItem("Settings") && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === "/admin/profile-settings"}
                  >
                    <Link
                      to="/admin/profile-settings"
                      className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground"
                    >
                      <Lock className="size-4" />
                      <span>Settings</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}


            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="shrink-0 border-t border-sidebar-border bg-muted/50 p-0 m-0">
          <SidebarMenu className="p-0 m-0">
            <SidebarMenuItem className="p-0 m-0">
              <DropdownMenu>
                <DropdownMenuTrigger className="flex w-full items-center gap-2 rounded-none px-2 py-1.5 text-sm outline-none border-0 bg-muted/50 transition-all hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus:outline-none focus:ring-0 focus:border-0 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage
                      src="https://media.istockphoto.com/id/2151517587/photo/hospital-clinic-patient-man-with-casual-tshirt-medical-hospital-3d-icon-illustration-render.webp?a=1&b=1&s=612x612&w=0&k=20&c=LGDaPN5miVyTIa31A3BUzo7Gm0GE4DbtkBdLylE32Ss="
                      alt="Admin"
                    />
                    <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-xs font-medium">
                      TW
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {user?.name || "TrackYourWheelz"}
                    </span>
                    <span className="truncate text-xs">
                      {user?.email || "admin@example.com"}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="min-w-56 w-[--radix-dropdown-menu-trigger-width] rounded-lg"
                  side="right"
                  align="end"
                >
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm ">
                      <Avatar className="h-8 w-8 rounded-lg">
                        <AvatarImage
                          src="https://media.istockphoto.com/id/2151517587/photo/hospital-clinic-patient-man-with-casual-tshirt-medical-hospital-3d-icon-illustration-render.webp?a=1&b=1&s=612x612&w=0&k=20&c=LGDaPN5miVyTIa31A3BUzo7Gm0GE4DbtkBdLylE32Ss="
                          alt="Admin"
                        />
                        <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-xs font-medium">
                          TW
                        </AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left text-sm leading-tight ">
                        <span className="truncate font-semibold">
                          {user?.name || "TrackYourWheelz"}
                        </span>
                        <span className="truncate text-xs capitalize">
                          Role: {user?.role || "Admin"}
                        </span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      asChild
                      className="text-foreground hover:text-foreground"
                    >
                      <Link
                        to="/admin/profile"
                        onClick={() => {
                          if (isMobile) {
                            setOpenMobile(false);
                          }
                        }}
                        className="no-underline text-foreground hover:text-foreground"
                      >
                        <User className="mr-2 h-4 w-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      asChild
                      className="text-foreground hover:text-foreground"
                    >
                      <Link
                        to="/admin/profile-settings"
                        onClick={() => {
                          if (isMobile) {
                            setOpenMobile(false);
                          }
                        }}
                        className="no-underline text-foreground hover:text-foreground"
                      >
                        <Lock className="mr-2 h-4 w-4" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      asChild
                      className="text-foreground hover:text-foreground"
                    >
                      <Link
                        to="/admin/settings"
                        onClick={() => {
                          if (isMobile) {
                            setOpenMobile(false);
                          }
                        }}
                        className="no-underline text-foreground hover:text-foreground"
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </DropdownMenuGroup>
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
